'use client';

import React, { useState } from 'react';
import { X, ChevronRight, ChevronLeft, Sparkles, Bookmark, UserCircle } from 'lucide-react';

interface OnboardingTourProps {
  onComplete: () => void;
}

interface TourSlide {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  description: string;
  steps?: string[];
  visual: React.ReactNode;
}

const slides: TourSlide[] = [
  {
    icon: <Sparkles size={20} style={{ color: '#C9A96E' }} />,
    title: 'Fashion Wizard',
    subtitle: 'Your personal stylist in 4 steps',
    description: 'Answer a few quick questions and Atelier\'s AI will curate a complete outfit tailored just for you.',
    steps: ['Choose your occasion', 'Set your budget', 'Select your style', 'Describe your look'],
    visual: (
      <div className="flex flex-col gap-2 w-full max-w-xs mx-auto">
        {['The Occasion', 'Your Budget', 'Style For', 'Your Look'].map((label, i) => (
          <div
            key={label}
            className="flex items-center gap-3 rounded-lg px-4 py-2.5"
            style={{
              backgroundColor: i === 0 ? '#C9A96E' : 'hsl(35 15% 92%)',
              opacity: 1 - i * 0.18,
            }}
          >
            <span
              className="w-5 h-5 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0"
              style={{
                backgroundColor: i === 0 ? 'rgba(255,255,255,0.3)' : 'hsl(35 20% 88%)',
                color: i === 0 ? '#fff' : '#1A1A1A',
              }}
            >
              {i + 1}
            </span>
            <span
              className="text-sm font-medium"
              style={{ color: i === 0 ? '#fff' : '#1A1A1A' }}
            >
              {label}
            </span>
          </div>
        ))}
      </div>
    ),
  },
  {
    icon: <Bookmark size={20} style={{ color: '#C9A96E' }} />,
    title: 'Saved Outfits',
    subtitle: 'Build your personal lookbook',
    description: 'Bookmark any outfit you love. Your saved looks appear in a quick-access carousel at the top of the wizard so you can revisit them anytime.',
    visual: (
      <div className="flex gap-3 w-full overflow-hidden justify-center">
        {[
          { title: 'Garden Party', occasion: 'Outdoor', price: '$320' },
          { title: 'Date Night', occasion: 'Evening', price: '$480' },
          { title: 'Office Chic', occasion: 'Work', price: '$260' },
        ].map((card, i) => (
          <div
            key={card.title}
            className="flex-shrink-0 rounded-xl p-3 w-28"
            style={{
              backgroundColor: '#fff',
              boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
              opacity: i === 2 ? 0.5 : 1,
            }}
          >
            <div
              className="w-full h-14 rounded-lg mb-2 flex items-center justify-center"
              style={{ backgroundColor: 'hsl(35 15% 92%)' }}
            >
              <Bookmark size={16} style={{ color: '#C9A96E' }} fill="#C9A96E" />
            </div>
            <p className="text-xs font-semibold truncate" style={{ color: '#1A1A1A' }}>{card.title}</p>
            <p className="text-xs" style={{ color: 'hsl(30 8% 52%)' }}>{card.occasion}</p>
            <p className="text-xs font-medium mt-1" style={{ color: '#C9A96E' }}>{card.price}</p>
          </div>
        ))}
      </div>
    ),
  },
  {
    icon: <UserCircle size={20} style={{ color: '#C9A96E' }} />,
    title: 'Account Settings',
    subtitle: 'Your profile, your way',
    description: 'Manage your email, update your password, and view a summary of your saved outfits — all from your account page.',
    visual: (
      <div
        className="w-full max-w-xs mx-auto rounded-2xl p-5"
        style={{ backgroundColor: '#fff', boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}
      >
        <div className="flex items-center gap-3 mb-4">
          <div
            className="w-10 h-10 rounded-full flex items-center justify-center font-serif-display text-lg"
            style={{ backgroundColor: '#C9A96E', color: '#fff' }}
          >
            A
          </div>
          <div>
            <p className="text-sm font-semibold" style={{ color: '#1A1A1A' }}>Your Profile</p>
            <p className="text-xs" style={{ color: 'hsl(30 8% 52%)' }}>member@atelier.com</p>
          </div>
        </div>
        {['Update Email', 'Change Password', 'Saved Outfits'].map((item, i) => (
          <div
            key={item}
            className="flex items-center justify-between py-2.5 border-b last:border-0"
            style={{ borderColor: 'hsl(35 20% 88%)' }}
          >
            <span className="text-xs font-medium" style={{ color: '#1A1A1A' }}>{item}</span>
            {i === 2 ? (
              <span
                className="text-xs font-semibold px-2 py-0.5 rounded-full"
                style={{ backgroundColor: 'hsl(35 15% 92%)', color: '#C9A96E' }}
              >
                3 saved
              </span>
            ) : (
              <ChevronRight size={14} style={{ color: 'hsl(30 8% 52%)' }} />
            )}
          </div>
        ))}
      </div>
    ),
  },
];

export default function OnboardingTour({ onComplete }: OnboardingTourProps) {
  const [current, setCurrent] = useState(0);
  const isLast = current === slides.length - 1;
  const slide = slides[current];

  const handleNext = () => {
    if (isLast) {
      onComplete();
    } else {
      setCurrent(c => c + 1);
    }
  };

  const handlePrev = () => {
    if (current > 0) setCurrent(c => c - 1);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(26,26,26,0.55)', backdropFilter: 'blur(4px)' }}
    >
      <div
        className="relative w-full max-w-md rounded-2xl overflow-hidden animate-fade-in-up"
        style={{ backgroundColor: '#FAF7F2', boxShadow: '0 24px 64px rgba(0,0,0,0.18)' }}
      >
        {/* Close */}
        <button
          onClick={onComplete}
          className="absolute top-4 right-4 z-10 p-1.5 rounded-full transition-all hover:opacity-70"
          style={{ backgroundColor: 'hsl(35 15% 92%)', color: 'hsl(30 8% 52%)' }}
          aria-label="Skip tour"
        >
          <X size={14} />
        </button>

        {/* Slide content */}
        <div className="px-6 pt-8 pb-6">
          {/* Icon + Title */}
          <div className="flex items-center gap-2 mb-1">
            {slide.icon}
            <span className="text-xs font-semibold uppercase tracking-widest" style={{ color: '#C9A96E', letterSpacing: '0.1em' }}>
              {slide.subtitle}
            </span>
          </div>
          <h2 className="font-serif-display text-2xl sm:text-3xl mb-3" style={{ color: '#1A1A1A' }}>
            {slide.title}
          </h2>
          <p className="text-sm leading-relaxed mb-6" style={{ color: 'hsl(30 8% 52%)' }}>
            {slide.description}
          </p>

          {/* Visual */}
          <div
            className="rounded-xl p-5 mb-6"
            style={{ backgroundColor: 'hsl(35 15% 92%)' }}
          >
            {slide.visual}
          </div>

          {/* Steps list (slide 1 only) */}
          {slide.steps && (
            <div className="flex flex-wrap gap-2 mb-6">
              {slide.steps.map((s, i) => (
                <span
                  key={s}
                  className="text-xs px-3 py-1 rounded-full font-medium"
                  style={{ backgroundColor: '#fff', color: '#1A1A1A', border: '1px solid hsl(35 20% 88%)' }}
                >
                  {i + 1}. {s}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          className="flex items-center justify-between px-6 py-4 border-t"
          style={{ borderColor: 'hsl(35 20% 88%)' }}
        >
          {/* Dots */}
          <div className="flex items-center gap-1.5">
            {slides.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrent(i)}
                className="rounded-full transition-all duration-300"
                style={{
                  width: i === current ? 20 : 6,
                  height: 6,
                  backgroundColor: i === current ? '#C9A96E' : 'hsl(35 20% 88%)',
                }}
                aria-label={`Go to slide ${i + 1}`}
              />
            ))}
          </div>

          {/* Navigation */}
          <div className="flex items-center gap-2">
            {current > 0 && (
              <button
                onClick={handlePrev}
                className="flex items-center gap-1 text-sm font-medium px-3 py-2 rounded-lg transition-all hover:opacity-70"
                style={{ color: 'hsl(30 8% 52%)' }}
              >
                <ChevronLeft size={15} />
                Back
              </button>
            )}
            <button
              onClick={handleNext}
              className="flex items-center gap-1.5 text-sm font-semibold px-5 py-2 rounded-lg transition-all hover:opacity-90 active:scale-95"
              style={{ backgroundColor: '#C9A96E', color: '#fff' }}
            >
              {isLast ? 'Get Started' : 'Next'}
              {!isLast && <ChevronRight size={15} />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
